import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useCmdbChain
 * Generic chaining controller for CMDB (Assets) dependent fields.
 *
 * Params:
 * - fieldsOrder: string[] ordered jiraField ids (e.g., [customfield_1, customfield_2, ...])
 * - getCurrentValue(fieldId): returns current selected option for a CMDB field (object { value, label }) or null
 * - setValue(fieldId, option): sets selected option for a CMDB field
 * - clearValue(fieldId): clears selected value for a CMDB field
 * - fetcher(fieldId, selectionsMap, userCtx): Promise<option[]> fetch options for fieldId based on previous selections
 * - userCtx: { sectors?: string[], role?: string }
 * - autoSelectSingle: boolean
 *
 * Returns:
 * - optionsByField: { [fieldId]: Array<{ value, label }> }
 * - loadingField: string | null
 * - loadOptionsFor(fieldId): Promise<void>
 * - handleSelect(fieldId, option): void
 * - clearAfter(fieldId): void
 */
export function useCmdbChain({
  fieldsOrder,
  getCurrentValue,
  setValue,
  clearValue,
  fetcher,
  userCtx,
  autoSelectSingle = true,
}) {
  const [optionsByField, setOptionsByField] = useState({});
  const [loadingField, setLoadingField] = useState(null);
  const orderRef = useRef(fieldsOrder || []);
  orderRef.current = fieldsOrder || [];
  const lastSigRef = useRef({});

  const fieldIndex = useCallback((fieldId) => orderRef.current.indexOf(fieldId), []);

  const selectionsUpTo = useCallback((fieldId) => {
    const idx = fieldIndex(fieldId);
    const map = {};
    if (idx <= 0) return map;
    for (let i = 0; i < idx; i += 1) {
      const fid = orderRef.current[i];
      const v = getCurrentValue(fid);
      if (v && (v.value || v.label)) map[fid] = v;
    }
    return map;
  }, [fieldIndex, getCurrentValue]);

  const clearOptionsAfter = useCallback((fieldId) => {
    const idx = fieldIndex(fieldId);
    if (idx < 0) return;
    setOptionsByField((prev) => {
      const copy = { ...prev };
      for (let i = idx + 1; i < orderRef.current.length; i += 1) {
        delete copy[orderRef.current[i]];
      }
      return copy;
    });
    // also clear cached signatures for subsequent fields
    try {
      for (let i = idx + 1; i < orderRef.current.length; i += 1) {
        delete lastSigRef.current[orderRef.current[i]];
      }
    } catch (_) {}
  }, [fieldIndex]);

  const clearAfter = useCallback((fieldId) => {
    const idx = fieldIndex(fieldId);
    if (idx < 0) return;
    for (let i = idx + 1; i < orderRef.current.length; i += 1) {
      clearValue(orderRef.current[i]);
    }
    clearOptionsAfter(fieldId);
  }, [clearValue, clearOptionsAfter, fieldIndex]);

  const buildSignature = useCallback((fieldId, selections) => {
    const keys = Object.keys(selections || {}).sort();
    const sel = keys.map((k) => `${k}:${selections[k]?.label || selections[k]?.value || ''}`).join('|');
    const role = userCtx?.role || '';
    const sectors = Array.isArray(userCtx?.sectors) ? userCtx.sectors.join(',') : '';
    return `${fieldId}::${sel}::${role}::${sectors}`;
  }, [userCtx]);

  const loadOptionsFor = useCallback(async (fieldId, overrideSelections) => {
    if (!fieldId) return;
    try {
      setLoadingField(fieldId);
      const prevSelections = overrideSelections || selectionsUpTo(fieldId);
      const signature = buildSignature(fieldId, prevSelections);
      const existing = optionsByField[fieldId];
      // If we already fetched for this exact signature (even if result was empty),
      // consider it a valid cached result and skip re-fetching to avoid infinite loops.
      if (existing !== undefined && lastSigRef.current[fieldId] === signature) {
        try {
          console.log('[useCmdbChain] loadOptionsFor skip (cached)', {
            fieldId,
            count: Array.isArray(existing) ? existing.length : -1,
          });
        } catch (_) {}
        return;
      }

      try {
        // Debug: show what we are about to request
        console.log('[useCmdbChain] loadOptionsFor', {
          fieldId,
          prevSelections,
          userCtx,
        });
      } catch (_) {}

      const fetched = await fetcher(fieldId, prevSelections, userCtx);

      try {
        console.log('[useCmdbChain] fetched options', {
          fieldId,
          count: Array.isArray(fetched) ? fetched.length : -1,
        });
      } catch (_) {}

      const current = getCurrentValue(fieldId);
      const merged = current ? [current, ...(Array.isArray(fetched) ? fetched : [])] : (Array.isArray(fetched) ? fetched : []);
      const seen = new Set();
      const dedup = [];

      for (const o of merged) {
        const key = o && o.value ? String(o.value) : '';
        if (!key || seen.has(key)) continue;

        seen.add(key);
        dedup.push({
          value: String(o.value),
          label: String(o.label),
          objectKey: o.key,
          key: o.key,
          attributes: o.attributes || []
        });
      }

      setOptionsByField((prev) => ({ ...prev, [fieldId]: dedup }));
      lastSigRef.current[fieldId] = signature;

      try {
        console.log('[useCmdbChain] dedup options', { fieldId, count: dedup.length });
      } catch (_) {}

      if (autoSelectSingle && dedup.length === 1 && (!current || current.value !== dedup[0].value)) {
        const autoOpt = dedup[0];
        try {
          console.log('[useCmdbChain] single option detected; simulating user selection', { fieldId, option: autoOpt });
        } catch (_) {}
        setValue(fieldId, autoOpt);
        // Clear subsequent fields because selection changed
        clearAfter(fieldId);
        try {
          console.log('[useCmdbChain] auto-selected single option', { fieldId, option: autoOpt });
        } catch (_) {}
        // Preload next field with up-to-date selections including this auto selection
        const idx = fieldIndex(fieldId);
        const nextId = idx >= 0 ? orderRef.current[idx + 1] : null;
        if (nextId) {
          // IMPORTANT: propagate previous selections used for this fetch,
          // not the possibly stale state-based selections.
          const nextSelections = { ...(prevSelections || {}) };
          nextSelections[fieldId] = autoOpt;
          try {
            console.log('[useCmdbChain] triggering next field fetch after auto-select', {
              fromField: fieldId,
              nextField: nextId,
              selections: nextSelections,
            });
          } catch (_) {}
          await loadOptionsFor(nextId, nextSelections);
        }
      }
    } catch (_) {
      setOptionsByField((prev) => ({ ...prev, [fieldId]: [] }));
    } finally {
      setLoadingField((curr) => (curr === fieldId ? null : curr));
    }
  }, [autoSelectSingle, buildSignature, fetcher, getCurrentValue, optionsByField, selectionsUpTo, setValue, userCtx]);

  const handleSelect = useCallback(async (fieldId, option) => {
    const prev = getCurrentValue(fieldId);
    const prevVal = prev?.value || '';
    const nextVal = option?.value || '';
    const hasChanged = prevVal !== nextVal;

    // Only clear / overwrite when the effective selection really changes
    if (hasChanged) {
      setValue(fieldId, option || null);
      clearAfter(fieldId);
    }

    try {
      console.log('[useCmdbChain] handleSelect', { fieldId, option, prev, hasChanged });
    } catch (_) {}

    // Preload next field options
    const idx = fieldIndex(fieldId);
    const nextId = idx >= 0 ? orderRef.current[idx + 1] : null;
    if (nextId) {
      const nextSelections = selectionsUpTo(nextId);
      if (option) nextSelections[fieldId] = option;
      try {
        console.log('[useCmdbChain] triggering next field fetch after user selection', {
          fromField: fieldId,
          nextField: nextId,
          selections: nextSelections,
        });
      } catch (_) {}
      await loadOptionsFor(nextId, nextSelections);
    }
  }, [clearAfter, fieldIndex, loadOptionsFor, selectionsUpTo, setValue, getCurrentValue]);

  // Prefetch first field options when order/userCtx changes (and cache prevents refetch)
  useEffect(() => {
    const firstId = orderRef.current && orderRef.current.length > 0 ? orderRef.current[0] : null;
    if (firstId) {
      loadOptionsFor(firstId);
    }
  }, [loadOptionsFor, fieldsOrder, userCtx]);

  // Enablement: a field is enabled only if all previous fields are answered
  const isPrevAnswered = useCallback((fieldId) => {
    const idx = fieldIndex(fieldId);
    if (idx <= 0) return true;
    for (let i = 0; i < idx; i += 1) {
      const prevId = orderRef.current[i];
      const v = getCurrentValue(prevId);
      if (!v || (!v.value && !v.label)) return false;
    }
    return true;
  }, [fieldIndex, getCurrentValue]);

  const enabledByField = useMemo(() => {
    const map = {};
    for (const fid of orderRef.current) {
      map[fid] = isPrevAnswered(fid);
    }
    return map;
  }, [isPrevAnswered]);

  const api = useMemo(() => ({
    optionsByField,
    loadingField,
    loadOptionsFor,
    handleSelect,
    clearAfter,
    enabledByField,
    isFieldEnabled: (fieldId) => Boolean(enabledByField?.[fieldId]),
  }), [clearAfter, handleSelect, loadOptionsFor, loadingField, optionsByField, enabledByField]);

  return api;
}

export default useCmdbChain;


